// お問い合わせの導線を、おちゃのこ標準の /contact から店のフリーページ（EcSiteForm）へ付け替える。
// wuSiteResource2 の各店 SiteCommon.ts の replaceForm() と、おちゃのこ「マーケティング →
// アクセス解析タグ設定」欄に直書きされていた jumpContact() の引き取り先（Issue #53 / #55）。
// 店ごとの表と判定は shared/contact-pages.ts。
//
// ★ グローバル関数は作らない。旧実装は input に onClick="jumpContact()" を文字列で貼っていたが、
//   TS 側の jumpContact は IIFE の中に閉じていてグローバルに出ておらず、実際に呼ばれていたのは
//   おちゃのこ側の直書きだけだった（おちゃのこ側を先に消すと「jumpContact is not defined」で止まる）。
//   ここでは click ハンドラを直接束ねる。OppHonpo(3) の TS が先にこの形に直している。
//
// 現行の挙動をそのまま写している:
//   - /contact に到達していたら redirect へ飛ぶ（redirect を持つ店だけ。pathname 判定）
//   - linkPrefixes で始まるリンク（ヘッダーの「お問い合わせ」も、商品ページの「この商品について問い合わせる」
//     <a class="inquiryinput" href=".../contact/product/N"> も）→ top（linkPrefixes の無い店はリンクを触らない）
//   - formPrefix で始まる form の action → form（formPrefix の無い店は form を触らない）
//   - 旧実装（おちゃのこ側）の /contact リダイレクトは DOM ready を待たずに走っていたが、hook は
//     DOMContentLoaded 後なので飛ぶのが少し遅くなる。他店も同じ条件で問題が出ていないので許容
//   - input[value='お問い合わせ'] の click → product へ location.replace（product の無い店は束ねない）
//     （2026-09-21 時点のあんしんやのテンプレートにはこの input が無く、商品ページの問い合わせは
//       上の <a> で top に飛ぶ。挙動を変えないため残している）

import { CONTACT, contactRedirectTarget } from '../shared/contact-pages'
import type { ContactPages } from '../shared/contact-pages'

export function applyContactRedirect(
  root: ParentNode,
  pages: ContactPages,
): { links: number; forms: number; inputs: number } {
  let links = 0
  if (pages.linkPrefixes && pages.top) {
    const top = pages.top
    for (const prefix of pages.linkPrefixes) {
      const found = root.querySelectorAll<HTMLAnchorElement>(`a[href^="${prefix}"]`)
      found.forEach((a) => a.setAttribute('href', top))
      links += found.length
    }
  }

  let forms = 0
  if (pages.formPrefix && pages.form) {
    const found = root.querySelectorAll<HTMLFormElement>(`form[action^="${pages.formPrefix}"]`)
    const action = pages.form
    found.forEach((f) => f.setAttribute('action', action))
    forms = found.length
  }

  const product = pages.product
  if (!product) return { links, forms, inputs: 0 }
  const inputs = root.querySelectorAll<HTMLInputElement>('input[value="お問い合わせ"]')
  inputs.forEach((input) => {
    input.addEventListener('click', () => {
      // 過渡期におちゃのこ側のグローバル jumpContact() がまだ居れば、旧実装の onClick 属性に任せる
      // （fukuro.in は旧実装が window.open なので、両方効くと「別タブが開いた上に今のタブも遷移」になる。Issue #60）。
      // 属性だけ残って実体が消えた状態（②③の順序違い）でも、こちらが効くので導線は死なない
      if (typeof (window as unknown as { jumpContact?: unknown }).jumpContact === 'function') return
      location.replace(product)
    })
  })
  return { links, forms, inputs: inputs.length }
}

export default function contactRedirect(): void {
  const siteId = window.wu?.siteInfoId
  const pages = siteId ? CONTACT[siteId] : undefined
  if (!pages) return
  const target = contactRedirectTarget(location.pathname, pages)
  if (target) {
    location.href = target
    return
  }
  applyContactRedirect(document, pages)
}
